import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chooseLanguage } from '~/lib/i18n';
import { SUPPORTED_LANGUAGES } from '~shared/i18n/languages';

/**
 * Переключатель языка (localization): кнопка с кодом текущего языка, по нажатию — список самоназваний.
 * При одном языке в проекте не показывается.
 * AICODE-NOTE: стили — классы `.lang*` в landing.css, не Tailwind: лендинг сбрасывает стили кнопок своим
 * `@scope (.ny-landing)`, утилиты Tailwind на нём не работают. Список, а не ряд кнопок — 9 самоназваний
 * в строку выталкивают остальную шапку за край экрана.
 */
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (SUPPORTED_LANGUAGES.length < 2) return null;
  // AICODE-NOTE: language, а не resolvedLanguage — тот берёт первый язык с переводами и при пустом каталоге показывает русский.
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  return (
    <div className="lang" ref={ref}>
      <button
        type="button"
        className="langbtn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('languageSwitcher.label')}: ${current.name}`}
        title={current.name}
        onClick={() => setOpen((v) => !v)}
      >
        {current.code.toUpperCase()}
      </button>
      {open && (
        <div className="langmenu" role="menu" aria-label={t('languageSwitcher.label')}>
          {SUPPORTED_LANGUAGES.map(({ code, name }) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              lang={code}
              aria-checked={code === current.code}
              className={code === current.code ? 'on' : undefined}
              onClick={() => {
                chooseLanguage(code);
                setOpen(false);
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
